import React from "react";

import { downloadPlot } from "@/components/VivoPlots/components/DownloadButton";
import { useTrackButtonClick } from "@/hooks/useTrackButtonClick";
import { Dropdown } from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import LegendIcon from "@/assets/icons/legend.svg?react";

interface PlotConfig {
  plotId: string;
  labels: { title: string };
}

interface FillScale {
  dataMapping?: unknown;
}

interface PlotControlsProps {
  showLegend: boolean;
  setShowLegend: (value: boolean) => void;
  editThisPlot: () => void;
  hidePlot: (plotId: string) => void;
  duplicatePlot?: ((plotId: string) => void) | null;
  inModal: boolean;
  fillScale?: FillScale | null;
  deletePlot: (plotId: string) => void;
  plotClassName: string;
  config: PlotConfig;
}

export const PlotControls = ({
  showLegend,
  setShowLegend,
  editThisPlot,
  hidePlot,
  duplicatePlot,
  inModal,
  fillScale,
  deletePlot,
  plotClassName,
  config,
}: PlotControlsProps) => {
  const trackButtonClick = useTrackButtonClick();
  return (
    <Dropdown
      menu={{
        items: [
          {
            label: (
              <div className="flex items-center gap-2">
                <LegendIcon />
                {showLegend ? "Hide legend" : "Show legend"}
              </div>
            ),
            key: "show-legend",
            onClick: () => {
              setShowLegend(!showLegend);
            },
            disabled: !fillScale?.dataMapping,
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <EditOutlined />
                Edit plot
              </div>
            ),
            key: "edit",
            onClick: () => {
              editThisPlot();
            },
          },
          ...(duplicatePlot
            ? [
                {
                  label: (
                    <div className="flex items-center gap-2">
                      <CopyOutlined />
                      Duplicate
                    </div>
                  ),
                  key: "duplicate",
                  onClick: () => {
                    duplicatePlot(config.plotId);
                  },
                },
              ]
            : []),
          {
            label: (
              <div className="flex items-center gap-2">
                <EyeInvisibleOutlined />
                Hide plot
              </div>
            ),
            key: "hide",
            onClick: () => {
              hidePlot(config.plotId);
            },
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <DownloadOutlined />
                Export
              </div>
            ),
            key: "download",
            onClick: () => {
              trackButtonClick("Download Plot", {
                plotClassName,
                plotTitle: config.labels.title,
              });
              downloadPlot(plotClassName, config.labels.title);
            },
          },

          {
            label: (
              <div className="flex items-center gap-2 text-[#980000]">
                <DeleteOutlined />
                Delete
              </div>
            ),
            key: "delete",
            onClick: () => {
              deletePlot(config.plotId);
            },
          },
        ].filter((item) => !inModal || item.key === "show-legend"),
      }}
    >
      <button
        type="button"
        data-testid="plot-controls-menu-trigger"
        aria-label="Plot menu"
        className="flex h-5 w-5 items-center justify-center rounded-sm text-[#2B4752]"
      >
        <EllipsisOutlined style={{ fontSize: "18px" }} />
      </button>
    </Dropdown>
  );
};
